import {
  createContext, useMemo, useState, MouseEvent, useCallback,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import { useTranslation } from 'react-i18next';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from '../../../4-module/widgets/portfolio/components/interfaces';
import { Divider } from '../../../2-component/divider/divider';
import {
  Button, Card, CardContent, Menu, MenuItem, Checkbox,
} from '../../../2-component';
import { Grid, Typography } from '../../../1-primative';
import { formatMoneyValue } from '../../../../util';
import RebalanceReportTable from './shared/rebalanceReportTable/rebalanceReportTable';
import {
  SecurityColumn,
  CurrentCashColumn,
  CurrentPercentColumn,
  CurrentHashColumn,
  TargetCashColumn,
  TargetPercentColumn,
  TargetHashColumn,
  DriftCashColumn,
  DriftPercentColumn,
  CurrentSubAccount,
  TradeSubAccount,
  PostSubAccount,
  TradeTableCell,
  ColumnTradeDataLog,
} from './shared/portfolioColumns';
import { useSinglePortfolioModel } from './shared/usePortfolioModel';
import { PortfolioWarnings } from '../../../4-module/widgets/portfolio/components/portfolioWarnings';
import { useThemeTokens } from '../../../../providers/themeTokenProvider';
import { buildNewDraftTrade } from '../../../4-module/widgets/portfolio/components/utils';

export const RebalanceResetContext = createContext(0);
export const RebalanceAutofillContext = createContext(0);
export const RebalanceDraftsContext = createContext<ColumnTradeDataLog[]>([]);
export const RebalanceOptimizedSecurities = createContext<PortfolioSecurity[]>([]);

const PortfolioPage = ({ item }: { item?: any }) => {
  const { id: paramId } = useParams();
  const id = item?.id || paramId;
  const { pathname } = useLocation();
  const { t } = useTranslation(['rebalanceReportV2']);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [displayDollar, setDisplayDollar] = useState<boolean>(true);
  const [displayPercent, setDisplayPercent] = useState<boolean>(true);
  const [displayHash, setDisplayHash] = useState<boolean>(false);

  const [resetTrades, setResetTrades] = useState<number>(0);
  const [autofillTrades, setAutofillTrades] = useState<number>(0);
  const [draftTrades, setDraftTrades] = useState<ColumnTradeDataLog[]>([]);

  const hasTradeChanges = useMemo(() => some(draftTrades, (trade) => trade.value !== 0), [draftTrades]);
  const totalItems = 0;

  const {
    loading,
    subAccounts,
    securities,
    securityFilteredSubAccount,
    displayInactive,
    setDisplayInactive,
    warnings,
    runSecuritiesOptimizer,
    securityOptimized,
    resetSecurityOptimized,
    executeTrades,
  } = useSinglePortfolioModel(id || '', pathname);

  const onTradeUpdated = useCallback((data: ColumnTradeDataLog): void => {
    setDraftTrades((prevTrades) => {
      const index = prevTrades.findIndex((trade) => trade.securityId === data.securityId && trade.subAccountId === data.subAccountId);
      if (index > -1) {
        const newTrades = [...prevTrades];
        newTrades[index] = data;
        return newTrades;
      }
      return [...prevTrades, data];
    });
  }, []);

  const finalDraftTrades: DraftSubTradeRequest[] = useMemo(() => {
    if (isEmpty(draftTrades) || isEmpty(securities)) return [];
    return securities.reduce((total: DraftSubTradeRequest[], security) => {
      const subAccount = security.subAccounts[0];
      const thisTrade = draftTrades.find((trade) => trade.securityId === security.id && trade.subAccountId === subAccount.id);
      if (!isEmpty(thisTrade) && security.id !== 'CASH') { total.push(buildNewDraftTrade(thisTrade?.postTrade || 0, subAccount, security)); }
      return total;
    }, []);
  }, [securities, draftTrades]);

  const defaultColumns = useMemo(() => [
    {
      header: t('columns.security'), accessor: 'security', sort: false, cell: (row: PortfolioSecurity) => <SecurityColumn data={row} />,
    }, {
      header: t('columns.currentDollar'), accessor: 'current_$', sort: false, cell: (row: PortfolioSecurity) => <CurrentCashColumn data={row} />,
    }, {
      header: t('columns.currentPercent'), accessor: 'current_%', sort: false, cell: (row: PortfolioSecurity) => <CurrentPercentColumn data={row} />,
    }, {
      header: t('columns.currentHash'), accessor: 'current_#', sort: false, visible: false, cell: (row: PortfolioSecurity) => <CurrentHashColumn data={row} />,
    }, {
      header: t('columns.targetDollar'), accessor: 'target_$', sort: false, cell: (row: PortfolioSecurity) => <TargetCashColumn data={row} />,
    }, {
      header: t('columns.targetPercent'), accessor: 'target_%', sort: false, cell: (row: PortfolioSecurity) => <TargetPercentColumn data={row} />,
    }, {
      header: t('columns.targetHash'), accessor: 'target_#', sort: false, visible: false, cell: (row: PortfolioSecurity) => <TargetHashColumn data={row} />,
    }, {
      header: t('columns.driftDollar'), accessor: 'drift_$', sort: false, cell: (row: PortfolioSecurity) => <DriftCashColumn data={row} />,
    }, {
      header: t('columns.driftPercent'), accessor: 'drift_%', sort: false, cell: (row: PortfolioSecurity) => <DriftPercentColumn data={row} />,
    },
  ], [t]);

  const subAccountColumns = useMemo(() => [
    {
      header: t('columns.currentDollar'), accessor: 'current_$', sort: false, cell: (row: PortfolioSecurity) => <CurrentSubAccount data={row} />,
    },
    {
      header: t('columns.tradeDollar'),
      accessor: 'trade_$',
      sort: false,
      CustomTableCell: TradeTableCell,
      cell: (row: PortfolioSecurity) => <TradeSubAccount onChange={onTradeUpdated} data={row} />,
    },
    {
      header: t('columns.postDollar'), accessor: 'post_$', sort: false, cell: (row: PortfolioSecurity) => <PostSubAccount data={row} />,
    },
  ], [onTradeUpdated, t]);

  const toggleAutoRebalance = (event: MouseEvent<HTMLButtonElement | HTMLButtonElement | HTMLLIElement>) => {
    setAnchorEl(isEmpty(anchorEl) && !isEmpty(event) ? event.currentTarget : null);
  };

  const { sys } = useThemeTokens();

  return (
    <Grid container gap={0}>
      <Grid item xs={12} mb={3}>
        <PortfolioWarnings portfolioWarnings={warnings} />
      </Grid>
      <Grid item xs={12} mb={5}>
        <Card loading={loading}>
          <CardContent>
            <Typography variant='headingLarge' mb={4}>
              {t('portfolio.portfolioDetails')}
            </Typography>

            <Grid container gap={1} flexWrap='nowrap'>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.accountName')}</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.accountType')}</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.taxability')}</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.value')}</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.settledCash')}</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>{t('portfolio.expectedCash')}</Typography></Grid>
            </Grid>
          </CardContent>
          { subAccounts.length > 0 && subAccounts.map((subAccount: PortfolioSubAccount) => (
            <>
              <Divider variant='fullWidth' />
              <CardContent>
                <Grid container gap={2} flexWrap='nowrap'>
                  <Grid item xs={2}>
                    <Typography>{subAccount.name}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{subAccount.holding?.financialProduct?.name}</Typography>
                  </Grid>
                  <Grid item xs={2} justifySelf='center'>
                    <Typography>-</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{subAccount.type}</Typography>
                  </Grid>
                  <Grid item xs={2} justifySelf=''>
                    <Typography>{formatMoneyValue(subAccount.cashCents)}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{formatMoneyValue(subAccount.expectedCashCents)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </>
          )) }
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card loading={loading}>
          <CardContent>

            <Grid container gap={0} justifyContent='space-between' alignItems='center'>
              <Grid item xs='auto'>
                <Grid container gap={2}>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayDollar}
                      onChange={() => setDisplayDollar((prev) => !prev)}
                      label={t('portfolio.displayDollar')}
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayPercent}
                      onChange={() => setDisplayPercent((prev) => !prev)}
                      label={t('portfolio.displayPercent')}
                    />
                  </Grid>
                  <Grid item xs='auto' style={{ display: 'none' }}>
                    <Checkbox
                      checked={displayHash}
                      onChange={() => setDisplayHash((prev) => !prev)}
                      label={t('portfolio.displayHash')}
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayInactive}
                      onChange={() => setDisplayInactive((prev) => !prev)}
                      label={t('portfolio.displayInactiveAccounts')}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs='auto'>
                <Grid container gap={2}>
                  <Grid item xs='auto'>
                    <Button
                      disabled={!hasTradeChanges}
                      leadingIcon={UndoIcon}
                      variant='outlined'
                      onClick={(event) => {
                        toggleAutoRebalance(event);
                        setResetTrades((counter) => counter + 1);
                        setAutofillTrades(0);
                        resetSecurityOptimized();
                      }}
                      label={t('actions.revert')}
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Button
                      label={t('actions.autoRebalance')}
                      trailingIcon={ArrowDropDownIcon}
                      variant='outlined'
                      aria-controls={open ? 'basic-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={open ? 'true' : undefined}
                      onClick={toggleAutoRebalance}
                    />

                    <Menu anchorEl={anchorEl} open={open} onClose={toggleAutoRebalance} disableAutoFocus={true} disableAutoFocusItem={true}>
                      <MenuItem
                        onClick={() => {
                          runSecuritiesOptimizer();
                        }}>
                        {t('actions.optimizer')}
                      </MenuItem>

                      {subAccounts.length === 1 && (
                        <MenuItem
                          onClick={(event) => {
                            toggleAutoRebalance(event);
                            setAutofillTrades((counter) => counter + 1);
                          }}>
                          {t('actions.autoFillToTarget')}
                        </MenuItem>
                      )}
                    </Menu>
                  </Grid>
                  <Grid item xs='auto'>
                    <Button
                      disabled={!hasTradeChanges}
                      label={t('actions.executeTrade')}
                      variant='filled'
                      onClick={async () => {
                        await executeTrades(finalDraftTrades);
                        navigate('/rebalanceReportV2/');
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>

          <Grid container gap={0} mt={3} alignItems='flex-end' flexWrap='nowrap'>
            <Grid
              item
              flexShrink={0}
              flexGrow={1}
              sx={{
                borderTop: `solid 1px ${sys.color.outlineVariant}`,
                borderLeft: `solid 1px ${sys.color.outlineVariant}`,
                '&:first-of-type': {
                  borderLeftColor: 'transparent',
                },
              }}
            >
              <Grid
                item
                sx={{ padding: '16px' }}
              >
                <Typography align='center' weight='bold'>&nbsp;</Typography>
              </Grid>
              <RebalanceReportTable<PortfolioSecurity>
                data={securities}
                columns={defaultColumns}
                loading={loading}
                checkbox={false}
                pagination={false}
                initialPage={1}
                linesPerPage={15}
                totalLines={totalItems}
              />
            </Grid>

            { subAccounts.map((subAccount) => (
              <Grid
                item
                flexShrink={0}
                flexGrow={1}
                sx={{
                  borderTop: `solid 1px ${sys.color.outlineVariant}`,
                  borderLeft: `solid 1px ${sys.color.outlineVariant}`,
                }}
              >
                <Grid
                  item
                  sx={{ padding: '16px' }}
                >
                  <Typography align='center' weight='bold'>{ subAccount.name }</Typography>
                </Grid>
                <RebalanceDraftsContext.Provider value={draftTrades}>
                  <RebalanceResetContext.Provider value={resetTrades}>
                    <RebalanceAutofillContext.Provider value={autofillTrades}>
                      <RebalanceOptimizedSecurities.Provider value={securityOptimized}>
                        <RebalanceReportTable<PortfolioSecurity>
                          data={securityFilteredSubAccount(subAccount.id)}
                          columns={subAccountColumns}
                          loading={loading}
                          checkbox={false}
                          pagination={false}
                          initialPage={1}
                          linesPerPage={15}
                          totalLines={totalItems}
                        />
                      </RebalanceOptimizedSecurities.Provider>
                    </RebalanceAutofillContext.Provider>
                  </RebalanceResetContext.Provider>
                </RebalanceDraftsContext.Provider>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PortfolioPage;
